import { useStoredPreference } from '@/features/reader/useStoredPreference';
import {
  CHAT_FOLLOWS_READING,
  CHAT_LANGUAGE_KEY,
  READING_LANGUAGE_KEY,
} from './settingsPrefs';

/**
 * Which language the assistant answers in.
 *
 * Settings offers the choice and writes it; until now nothing read it, so a
 * reader who set reading to Malayalam and the assistant to English still got
 * Malayalam answers. The preference existed, was previewed, and did nothing.
 *
 * `same-as-reading` is stored as that literal rather than as a resolved code,
 * so changing the reading language carries the assistant with it — which is
 * what "follows" has to mean to be worth having. Resolving it here, in one
 * place, is what keeps the reader's docked chat and the standalone screen
 * from disagreeing about which language a question is asked in.
 *
 * @param activeReadingLanguage the language actually on screen, where a
 *        khutbah is open. The reader's comes from the URL, so a shared link
 *        or the language picker changes it without touching the stored
 *        default — and "same as reading" has to follow the khutbah being
 *        read, not the one usually read.
 */
const isLanguage = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-z]{2,3}$/.test(value);

const isChoice = (value: unknown): value is string =>
  value === CHAT_FOLLOWS_READING || isLanguage(value);

export function useAssistantLanguage(activeReadingLanguage?: string): string {
  const [storedReading] = useStoredPreference(READING_LANGUAGE_KEY, 'ml', isLanguage);
  const [chat] = useStoredPreference(CHAT_LANGUAGE_KEY, CHAT_FOLLOWS_READING, isChoice);

  // "Reading" means what is being read NOW, not what the reader usually
  // reads. The reader takes its language from the URL — that is what makes
  // "this khutbah in Malayalam" a working link — so a stored default of `ml`
  // opened at `?language=en` was showing English and asking in Malayalam.
  //
  // Surfaces with no khutbah open pass nothing and fall back to the stored
  // default, which is the only reading language they have.
  const reading = isLanguage(activeReadingLanguage) ? activeReadingLanguage : storedReading;

  return chat === CHAT_FOLLOWS_READING ? reading : chat;
}
